import React from 'react';
import { Dialog, DialogContent } from '@material-ui/core';

import { SystemIconButton } from './ActionButton';
import { dealTokens } from './designTokens';

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M19 12H5m6 6-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// PageIntro — the one-or-two-sentence explanation of what a page is for, presented as a
// message to be read rather than as grey sub-text under a heading.
//
// It exists because the redesigned pages stopped repeating their tab name as an on-page
// <h1> (the title now lives in the L1 breadcrumb, TopNav.js). That freed the header row
// for the description — but a muted 13px paragraph floating on the page canvas reads as
// leftover caption text and gets skipped, which is the opposite of the point.
//
// So it is given a surface of its own: a tinted ground, a solid rule down the leading edge
// and an icon, with the text at full `color.text` weight instead of muted. That is enough
// to read as an addressed message without competing with the page's primary action — no
// heavier treatment (full fill, bold copy, a dismiss control) is warranted for something
// shown on every visit.
//
// SKY BLUE (`color.note` / `color.noteSoft`), not the brand coral: coral is this product's
// primary/destructive-adjacent accent, so a coral-tinted band with a rule down its side
// reads as a warning the user is supposed to act on. It was green (`color.success`) from
// 2026-09-20, but green reads as a success/confirmation message; a light sky blue
// carries "here is something useful to know" without implying anything happened or is
// wrong. Not `color.info`: that indigo is the Edit action colour. Owner call, 2026-09-24,
// tint as a left→right fade to transparent.
//
// One shared implementation on purpose: this is meant to land on every page family, and a
// per-page copy is exactly how the header row drifted the first time.
const IntroIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="7.75" r="1.15" fill="currentColor" />
  </svg>
);

export const PageIntro = ({ children, icon = <IntroIcon />, style }) => (
  <div
    // `role="note"` (not "alert"/"status"): this is standing explanatory copy, present on
    // every visit — announcing it as a live region would interrupt a screen reader user
    // on every navigation.
    role="note"
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: dealTokens.space[3],
      minWidth: 0,
      padding: `${dealTokens.space[3]}px ${dealTokens.space[4]}px`,
      // Fades from the tint to transparent left→right, so the band anchors on the icon and
      // rule and dissolves into the page instead of ending in a hard-edged box.
      background: `linear-gradient(90deg, ${dealTokens.color.noteSoft} 0%, transparent 100%)`,
      borderLeft: `3px solid ${dealTokens.color.note}`,
      borderRadius: dealTokens.radius.md,
      color: dealTokens.color.text,
      ...style,
    }}
  >
    {icon && <span style={{ display: 'flex', color: dealTokens.color.note }}>{icon}</span>}
    <p style={{ margin: 0, minWidth: 0, fontSize: 13.5, lineHeight: 1.55 }}>{children}</p>
  </div>
);

// Omitting `title` is how a page opts into the breadcrumb-owns-the-title treatment: on a
// page reached through an L2 tab, an <h1> repeating that tab's own word says the same thing
// twice, so the title moves up into the L1 breadcrumb (TopNav.js) and the description is
// promoted to carry the row on its own — as a PageIntro message, since a muted sub-line
// with nothing above it reads as an orphaned caption and gets skipped.
//
// Keep the title on a page that ISN'T a tab, or whose real name differs from the tab that
// leads to it; there the description stays the quiet sub-line under the heading.
export const SystemPageHeader = ({ title, description, action, children, style, size = 'default' }) => (
  <header
    style={{
      display: 'flex',
      alignItems: title ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      gap: dealTokens.space[4],
      marginBottom: dealTokens.layout.sectionGap,
      ...style,
    }}
  >
    <div style={{ minWidth: 0, flex: 1 }}>
      {title && (
        <h1
          style={{
            margin: 0,
            color: dealTokens.color.text,
            fontSize: size === 'compact' ? 15 : 24,
            fontWeight: dealTokens.font.weightSemibold,
            lineHeight: 1.25,
          }}
        >
          {title}
        </h1>
      )}
      {description &&
        (title ? (
          <p style={{ margin: '6px 0 0', color: dealTokens.color.textMuted, fontSize: 13, lineHeight: 1.5 }}>{description}</p>
        ) : (
          <PageIntro>{description}</PageIntro>
        ))}
      {children}
    </div>
    {action && <div style={{ display: 'flex', alignItems: 'center', gap: dealTokens.space[2], flexShrink: 0 }}>{action}</div>}
  </header>
);

export const SystemCard = ({ children, padding = dealTokens.layout.cardPadding, style, ...props }) => (
  <section
    style={{
      minWidth: 0,
      padding,
      border: `1px solid ${dealTokens.color.border}`,
      borderRadius: dealTokens.radius.lg,
      background: dealTokens.color.surface,
      ...style,
    }}
    {...props}
  >
    {children}
  </section>
);

// `preview` is an optional slot above icon/title/description for a richer thumbnail than a
// single icon can convey (e.g. a scaled-down render of the actual thing being chosen) —
// added for the dashboard's "Add widget" picker; every other caller only ever used
// icon/title/description, so this is additive and doesn't change their rendering.
export const SystemChoiceCard = ({ icon, title, description, preview, selected = false, disabled = false, onClick, style }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      width: '100%',
      minHeight: 150,
      padding: dealTokens.space[5],
      border: `1px solid ${selected ? dealTokens.color.primary : dealTokens.color.border}`,
      borderRadius: dealTokens.radius.lg,
      background: selected ? dealTokens.color.primarySoft : dealTokens.color.surface,
      color: dealTokens.color.text,
      textAlign: 'left',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition: `border-color ${dealTokens.motion.fast}, background ${dealTokens.motion.fast}`,
      ...style,
    }}
    onMouseEnter={event => {
      if (!disabled && !selected) event.currentTarget.style.borderColor = dealTokens.color.primary;
    }}
    onMouseLeave={event => {
      if (!selected) event.currentTarget.style.borderColor = dealTokens.color.border;
    }}
  >
    {preview && <span style={{ display: 'block', marginBottom: dealTokens.space[3] }}>{preview}</span>}
    {icon && <span style={{ display: 'inline-flex', marginBottom: dealTokens.space[3], color: dealTokens.color.primary }}>{icon}</span>}
    <span style={{ display: 'block', marginBottom: 6, fontSize: 14, fontWeight: dealTokens.font.weightSemibold, lineHeight: 1.35 }}>{title}</span>
    {description && <span style={{ display: 'block', color: dealTokens.color.textMuted, fontSize: 12.5, lineHeight: 1.5 }}>{description}</span>}
  </button>
);

export const SystemModalHeader = ({ title, description, onBack, backLabel = 'Back', action, style }) => (
  <header
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: dealTokens.space[3],
      padding: dealTokens.layout.modalHeaderPadding,
      background: dealTokens.color.surface,
      flexShrink: 0,
      ...style,
    }}
  >
    {onBack && <SystemIconButton label={backLabel} icon={<BackIcon />} onClick={onBack} />}
    <div style={{ minWidth: 0, flex: 1 }}>
      <h2 style={{ margin: 0, color: dealTokens.color.text, fontSize: 20, fontWeight: dealTokens.font.weightSemibold, lineHeight: 1.3 }}>{title}</h2>
      {description && <p style={{ margin: '5px 0 0', color: dealTokens.color.textMuted, fontSize: 13, lineHeight: 1.5 }}>{description}</p>}
    </div>
    {action && <div style={{ flexShrink: 0 }}>{action}</div>}
  </header>
);

export const SystemModalBody = ({ children, style }) => (
  <div
    style={{
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: dealTokens.layout.modalBodyPadding,
      background: dealTokens.color.surface,
      ...style,
    }}
  >
    {children}
  </div>
);

export const SystemModalFooter = ({ children, style }) => (
  <footer
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: dealTokens.space[2],
      padding: dealTokens.layout.modalFooterPadding,
      borderTop: `1px solid ${dealTokens.color.border}`,
      background: dealTokens.color.surface,
      flexShrink: 0,
      ...style,
    }}
  >
    {children}
  </footer>
);

// Form + calendar-style summary. Flex wrapping uses the space inside the modal,
// so narrow dialogs stack even on a wide desktop. One body scroll owns both.
export const SystemFormSummaryLayout = ({ children, summary, summaryLabel }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: dealTokens.space[6], alignItems: 'flex-start', width: '100%' }}>
    <div style={{ flex: '2 1 480px', minWidth: 0 }}>{children}</div>
    <aside aria-label={summaryLabel} style={{ flex: '1 1 320px', minWidth: 0, position: 'sticky', top: 0 }}>
      {summary}
    </aside>
  </div>
);

export const SystemDialog = ({
  open = true,
  onClose,
  onBack = onClose,
  backLabel,
  title,
  description,
  headerAction,
  children,
  footer,
  width = 720,
  height = 'auto',
  maxHeight = '92vh',
  bodyStyle,
  paperStyle,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={false}
    PaperProps={{
      style: {
        width,
        maxWidth: 'calc(100vw - 40px)',
        height,
        maxHeight,
        borderRadius: dealTokens.radius.lg,
        overflow: 'hidden',
        background: dealTokens.color.surface,
        fontFamily: dealTokens.font.family,
        display: 'flex',
        flexDirection: 'column',
        ...paperStyle,
      },
    }}
  >
    <DialogContent
      style={{
        padding: 0,
        minHeight: 0,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden',
        background: dealTokens.color.surface,
      }}
    >
      {title && <SystemModalHeader title={title} description={description} onBack={onBack} backLabel={backLabel} action={headerAction} />}
      <SystemModalBody style={bodyStyle}>{children}</SystemModalBody>
      {footer && <SystemModalFooter>{footer}</SystemModalFooter>}
    </DialogContent>
  </Dialog>
);
